'use client';

import { useState } from 'react';
import { faArrowDownShortWide } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { searchSortStyles } from './search-sort.styles';

import { Icon } from '@/components/ui/icon.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { Sheet, SheetClose, SheetContent } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import {
  SearchModalityAnalyticsMap,
  SearchSortAnalyticsMap,
  SearchSortMap,
  SearchSortType,
  searchSortOptions,
} from '@/app/(main)/(pages)/busca/[slug]/_types/search-sort.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { SearchType } from '@/types/most-search-type';
import useDeviceType from '@/hooks/use-device-type.hook';

interface SearchSortProps {
  value: SearchSortType;
  onChange: (value: SearchSortType) => void;
  modality: SearchType;
  hiddenOptions?: SearchSortType[];
  className?: string;
  dataTestId?: string;
}

export function SearchSort({
  value,
  onChange,
  modality,
  hiddenOptions = [],
  className,
  dataTestId,
}: SearchSortProps) {
  const [open, setOpen] = useState(false);
  const styles = searchSortStyles();
  const analytics = getAnalyticsPlugin();
  const deviceType = useDeviceType();

  const visibleOptions = searchSortOptions.filter((option) => !hiddenOptions.includes(option));

  const isActive = value !== SearchSortType.RELEVANCE;
  const triggerLabel = isActive ? SearchSortMap[value].name : 'Ordenar';

  const handleOpen = (): void => {
    analytics.track(AnalyticsEnum.SORT_OPEN_SELECTOR, {
      ordenacao_atual: SearchSortAnalyticsMap[value],
      modalidade: SearchModalityAnalyticsMap[modality],
      device_type: deviceType,
    });
    setOpen(true);
  };

  const handleSelect = (option: SearchSortType): void => {
    if (option !== value) {
      analytics.track(AnalyticsEnum.SORT_SELECTED, {
        tipo_ordenacao: SearchSortAnalyticsMap[option],
        ordenacao_anterior: SearchSortAnalyticsMap[value],
        modalidade: SearchModalityAnalyticsMap[modality],
        device_type: deviceType,
      });
    }

    onChange(option);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Pill
        hasAction
        variant={isActive ? 'success' : 'light'}
        icon={faArrowDownShortWide}
        label={triggerLabel}
        action={handleOpen}
        className={className}
        data-testid={dataTestId ?? DataTestIdEnum.SEARCH_SORT_TRIGGER}
      />

      <SheetContent side="centerFit" className="p-0 sm:max-w-[22.5rem]" hasHeader title="Ordenar">
        <div className="flex w-full items-start gap-2 px-5 pb-4 pt-5" data-testid={DataTestIdEnum.SEARCH_SORT_MODAL}>
          <div className="flex flex-1 items-center gap-2">
            <Icon icon={faArrowDownShortWide} className="text-2xl text-green-500" aria-hidden />
            <Typography as="span" variant={TypographyVariant.MODAL_TITLE}>
              Ordenar
            </Typography>
          </div>
          <SheetClose asChild>
            <ModalCloseButton />
          </SheetClose>
        </div>

        <div className={styles.grid()}>
          {visibleOptions.map((option) => {
            const selected = option === value;
            const { name, icon } = SearchSortMap[option];

            return (
              <button
                key={option}
                type="button"
                className={styles.option()}
                aria-pressed={selected}
                onClick={() => handleSelect(option)}
                data-testid={`${DataTestIdEnum.SEARCH_SORT_OPTION}-${option}`}
              >
                <span className={styles.iconBox({ selected })}>
                  <Icon icon={icon} className={styles.icon()} aria-hidden />
                </span>
                <span className={styles.label({ selected })}>{name}</span>
              </button>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
